import { Component, computed, inject, signal } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { DateTime } from 'luxon';
import { catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, Observable, of } from 'rxjs';
import { BranchService } from '../../../service/branch.service';
import { LocationGroupService } from '../../../service/location-group.service';
import { Branch, ReviewManagementBranch } from '../../../types';
import { LocationGroup } from '../../../types/location-group-response';

export const USAGE_FILTERS_DEBOUNCE_MS = 750;

const EMPTY_USAGE: ReviewManagementBranch = { items: [], intervals: [] };

@Component({
  selector: 'app-review-management',
  templateUrl: './review-management.component.html',
  styleUrls: ['./review-management.component.scss'],
  standalone: false,
})
export class ReviewManagementComponent {
  private branchService = inject(BranchService);
  private locationGroupService = inject(LocationGroupService);

  public selectedBranches = signal<Branch[]>([]);
  public selectedLocationGroup = signal<LocationGroup | undefined>(undefined);
  public reloadingBranchIds = signal<number[]>([]);

  // the endpoints accept several groups, but the page only lets people pick one
  private selectedLocationGroupIds = computed(() => {
    const group = this.selectedLocationGroup();
    return group ? [group.id] : [];
  });

  public pageReviewsOrIncidents = linkedQueryParam<'reviews' | 'incidents'>('show', {
    defaultValue: 'reviews',
  });

  readonly pageBranchIds = linkedQueryParam<number[]>('branch_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  readonly pageLocationGroupIds = linkedQueryParam<string[]>('location_group_ids', {
    parse: (value) => (value ? value.split(',') : []),
    stringify: (value) => value.join(','),
  });

  public startOfWeek = computed(() => DateTime.now().startOf('week').set({ hour: 8 }).toJSDate());

  public hasFilters = computed(() => this.selectedBranches().length > 0 || this.selectedLocationGroupIds().length > 0);

  // picking several branches or changing the location group in a row should fetch once instead of once per pick
  private usageFilters = toSignal(
    toObservable(
      computed(() => ({
        branchIds: this.selectedBranches().map((b) => b.id),
        locationGroupIds: this.selectedLocationGroupIds(),
      })),
    ).pipe(debounceTime(USAGE_FILTERS_DEBOUNCE_MS), distinctUntilChanged(isEqual)),
    { initialValue: { branchIds: [], locationGroupIds: [] } },
  );

  public usage = rxResource({
    params: () => this.usageFilters(),
    stream: ({ params }) => this.getUsage(params.branchIds, params.locationGroupIds),
    defaultValue: EMPTY_USAGE,
  });

  updatePageBranchIds() {
    this.pageBranchIds.set(this.selectedBranches().map((b) => b.id));
  }

  updatePageLocationGroupIds() {
    this.pageLocationGroupIds.set(this.selectedLocationGroupIds());
  }

  reloadBranch(branchId: number) {
    const locationGroupIds = this.selectedLocationGroupIds();
    this.reloadingBranchIds.update((ids) => [...ids, branchId]);
    this.getUsage([branchId], locationGroupIds)
      .pipe(finalize(() => this.reloadingBranchIds.update((ids) => ids.filter((id) => id !== branchId))))
      .subscribe((branchUsage) => {
        // the selection changed while reloading, so the whole page is already being refreshed
        if (!isEqual(locationGroupIds, this.selectedLocationGroupIds())) {
          return;
        }

        const reloaded = branchUsage.items.find((item) => item.id === branchId);
        this.usage.update((current) => ({
          ...current,
          items: current.items.map((item) => (item.id === branchId ? (reloaded ?? { ...item, locations: [] }) : item)),
        }));
      });
  }

  private getUsage(branchIds: number[], locationGroupIds: string[]): Observable<ReviewManagementBranch> {
    if (locationGroupIds.length > 0) {
      return this.locationGroupService.getLocationGroupsUsage({ location_group_ids: locationGroupIds, branch_ids: branchIds });
    }
    if (branchIds.length === 0) {
      return of(EMPTY_USAGE);
    }

    // a failing branch shouldn't prevent the rest from rendering
    return forkJoin(branchIds.map((id) => this.branchService.getBranchUsage(id).pipe(catchError(() => of(EMPTY_USAGE))))).pipe(
      map((usages) => ({
        items: usages.flatMap((u) => u.items),
        intervals: usages.find((u) => u.intervals.length > 0)?.intervals ?? [],
      })),
    );
  }
}
